import store from "@saas/store/src"; // 导入 store 实例
// 是否是白板和录屏用户
export const isWhiteboardAndRecord = (userId) => {
  return userId.startsWith("whiteboard_") || userId.startsWith("record_");
};

// 判断id 是否是助教
export const isAssistant = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  if (id && roomInfo.assistant_teacher_id_arr) {
    return roomInfo.assistant_teacher_id_arr.includes(id);
  }
  return false;
};

// 判断id 是否是老师
export const isTeacher = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  if (id && roomInfo.teacher_id) {
    return roomInfo.teacher_id === id;
  }
  return false;
};
// 判断id是否是学生
export const isStudent = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  const idList = [
    ...(roomInfo.assistant_teacher_id_arr || []),
    roomInfo.teacher_id,
  ];
  if (idList.includes(id)) {
    return false;
  }
  return true;
};
// 判断是否有操作某人上下台的权限，，助教只能操作学生
export const hasUpDownStagePermission = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  if (roomInfo.isTeacher) {
    // 老师不能操作自己，可以操作其他人包括学生和助教
    return id !== roomInfo.teacher_id;
  } else if (roomInfo.isAssistant) {
    // 助教只能操作学生,不能操作老师和助教
    return isStudent(id, roomInfo);
  } else {
    return false;
  }
};
//
// 判断是否有操作某人麦克风的权限，老师可以操作所有人包括自己，助教只能操作学生和自己
export const hasAudioPermission = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  const userStatus = store.state.live.userStatus;
  if (roomInfo.isTeacher) {
    return true;
  } else if (roomInfo.isAssistant) {
    return id === userStatus.user_id || isStudent(id, roomInfo);
  } else {
    return false;
  }
};

// 判断是否有操作某人摄像头的权限，老师可以操作所有人包括自己，助教只能操作学生和自己
export const hasVideoPermission = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  const userStatus = store.state.live.userStatus;
  if (roomInfo.isTeacher) {
    return true;
  } else if (roomInfo.isAssistant) {
    return id === userStatus.user_id || isStudent(id, roomInfo);
  } else {
    return false;
  }
};
// 判断是否有操作某人白板的权限，老师可以操作所有人包括自己，助教只能操作学生和自己
export const hasWhiteboardPermission = (id, roomInfo) => {
  roomInfo = roomInfo || store.state.live.roomInfo;
  if (roomInfo.isTeacher) {
    // 老师不能操作自己，可以操作其他人包括学生和助教
    return id !== roomInfo.teacher_id;
  } else if (roomInfo.isAssistant) {
    // 助教只能操作学生,不能操作老师和助教
    return isStudent(id, roomInfo);
  } else {
    return false;
  }
};

/**
 * 深度比较两个对象的值是否相等
 * 不关心属性顺序，只要每个属性的值相等就返回 true
 *
 * @param {*} obj1 - 第一个对象
 * @param {*} obj2 - 第二个对象
 * @param {Object} options - 配置选项
 * @param {boolean} options.strict - 是否严格模式（默认 false，会进行类型转换比较）
 * @returns {boolean} 是否相等
 *
 * @example
 * isEqual({ a: 1, b: 2 }, { b: 2, a: 1 }) // true
 * isEqual({ a: 1, b: { c: 2 } }, { a: 1, b: { c: 2 } }) // true
 * isEqual([1, 2, 3], [1, 2, 3]) // true
 * isEqual(null, null) // true
 * isEqual(undefined, undefined) // true
 */
export function isEqual(obj1, obj2, options = {}) {
  const { strict = false } = options;

  // 如果两个值严格相等（包括基本类型和引用类型）
  if (obj1 === obj2) {
    return true;
  }

  // 处理 null 和 undefined
  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  // 类型不同，在非严格模式下尝试转换
  if (typeof obj1 !== typeof obj2) {
    if (!strict) {
      // 尝试转换为字符串比较
      return String(obj1) === String(obj2);
    }
    return false;
  }

  // 处理数组
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) {
      return false;
    }

    // 数组需要按顺序比较
    for (let i = 0; i < obj1.length; i++) {
      if (!isEqual(obj1[i], obj2[i], options)) {
        return false;
      }
    }
    return true;
  }

  // 处理对象
  if (
    typeof obj1 === "object" &&
    typeof obj2 === "object" &&
    !Array.isArray(obj1) &&
    !Array.isArray(obj2)
  ) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // 键的数量不同
    if (keys1.length !== keys2.length) {
      return false;
    }

    // 检查每个键的值
    for (const key of keys1) {
      if (!keys2.includes(key)) {
        return false;
      }

      if (!isEqual(obj1[key], obj2[key], options)) {
        return false;
      }
    }

    return true;
  }

  // 其他类型（基本类型）
  if (strict) {
    return obj1 === obj2;
  }

  // 非严格模式下的类型转换比较
  return String(obj1) === String(obj2);
}

/**
 * 判断对象a是否被对象b包含（a的所有键值对都存在于b中，且值相等）
 * @param {Object} a - 被包含的对象（需完全存在于b中）
 * @param {Object} b - 包含方对象
 * @returns {boolean} - a是否被b包含
 *
 */
export function isObjectIncluded(a, b) {
  console.log("isObjectIncluded__", a, b);
  // 类型校验：必须是纯对象（排除数组、null等）
  const isPlainObject = (obj) =>
    Object.prototype.toString.call(obj) === "[object Object]";

  if (!isPlainObject(a) || !isPlainObject(b)) {
    return false;
  }

  // 获取a的所有键
  const aKeys = Object.keys(a);

  // 1. 检查a的所有键是否都存在于b中
  if (!aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key))) {
    return false;
  }

  // 2. 检查a和b中对应键的值是否相等（支持嵌套对象）
  return aKeys.every((key) => {
    const aValue = a[key];
    const bValue = b[key];

    // 若值是对象，递归判断包含关系
    if (isPlainObject(aValue) && isPlainObject(bValue)) {
      return isObjectIncluded(aValue, bValue);
    }

    // 非对象值直接比较（基本类型、数组、函数等）
    // 注意：数组会按引用比较，若需深度比较数组可扩展
    return aValue === bValue;
  });
}

/**
 * 判断两个对象 是否有包含关系
 * @param {Object} a -
 * @param {Object} b -
 * @returns {boolean} - a和b是否有包含关系
 *
 */
export function checkInclusion(a, b) {
  return isObjectIncluded(a, b) || isObjectIncluded(b, a);
}

// 是否过滤某条消息
export function filterMessageByLessonId(userId, lessonId, roomInfo) {
  roomInfo = roomInfo || store.state.live.roomInfo;
  const userStatus = store.state.live.userStatus;
  if (
    roomInfo.isStudent &&
    userId !== userStatus.user_id &&
    lessonId !== roomInfo.lesson_id &&
    isStudent(userId)
  ) {
    // 自己是学生，消息的来源也是学生，userid和当前用户不一致，并且消息的课时id和当前课时id不一致，则过滤,
    return true;
  }
  return false;
}
